'use client';

import { ExpressCheckoutElement, PaymentElement } from '@stripe/react-stripe-js';
import { getExpressButtonTheme } from '@/lib/checkout/express-button-theme';

/**
 * Shared payment-method UI for every Stripe checkout surface in the app.
 *
 * Renders, in order:
 *   1. ExpressCheckoutElement - Stripe's official native wallet buttons
 *      (Apple Pay / Google Pay / Link), shown only on devices that
 *      support the relevant wallet. Stripe handles the visibility.
 *   2. A "Or pay with card" divider.
 *   3. A card-only PaymentElement - other card-family methods the
 *      Stripe account has enabled (eg SEPA, iDEAL) still render
 *      inside the accordion, but the wallet rows are explicitly
 *      suppressed so they don't appear twice.
 *
 * Why this exists: every surface that takes a payment was previously
 * rolling its own <PaymentElement options={{paymentMethodOrder: [
 * 'apple_pay','google_pay','link','card']}} />, which renders wallets
 * as small accordion rows instead of the proper tall native buttons.
 * Visual treatment drifted from one checkout to the next and bug fixes
 * had to be applied surface-by-surface. This component is the single
 * source of truth for "how a payment looks on Fitvent".
 *
 * Consumers wrap their own form + submit button + backend confirm
 * logic around this. The wallet path's onConfirm calls back into the
 * consumer so card submit and wallet submit run identical flows.
 */
export interface PaymentMethodsRowProps {
  /**
   * T&C / consent gate. If false, wallet buttons render at reduced
   * opacity and a tap is silently rejected (without calling resolve()
   * the native wallet sheet never opens). Defaults to true for
   * surfaces that don't have a separate consent step.
   */
  termsAccepted?: boolean;

  /**
   * Page-context theme - drives wallet button colour (Apple/Google
   * each publish black + white variants; we pick the one with enough
   * contrast against the surrounding background).
   */
  contentBoxStyle?: 'white' | 'black' | 'transparent';
  themeText?: string;

  /**
   * Called when a wallet button is tapped and Stripe completes the
   * native sheet. Consumers wire this to the same callback as their
   * card submit button so both paths run the same elements.submit() +
   * stripe.confirmPayment() + backend-confirm sequence.
   */
  onWalletConfirm: () => void | Promise<void>;

  /**
   * Reports the card PaymentElement's completeness (all required fields valid).
   * Consumers use it to disable their card submit button until details are
   * entered. The wallet path has its own button and is unaffected.
   */
  onCardCompleteChange?: (complete: boolean) => void;
}

export function PaymentMethodsRow({
  termsAccepted = true,
  contentBoxStyle,
  themeText,
  onWalletConfirm,
  onCardCompleteChange,
}: PaymentMethodsRowProps) {
  const buttonThemes = getExpressButtonTheme(contentBoxStyle, themeText);

  return (
    <>
      <div
        className={`transition-opacity ${!termsAccepted ? 'opacity-60' : ''}`}
        aria-disabled={!termsAccepted}
      >
        <ExpressCheckoutElement
          options={{
            buttonType: { applePay: 'buy', googlePay: 'buy' },
            buttonTheme: buttonThemes,
            // Link is deliberately EXCLUDED from the wallet row: its express button
            // runs a full-page redirect to a Stripe-hosted checkout (poor UX),
            // and Link is already offered embedded inside the card
            // PaymentElement below. Apple/Google Pay stay native-sheet.
            paymentMethods: { applePay: 'always', googlePay: 'always', link: 'never' },
            layout: { maxColumns: 1, maxRows: 3 },
          }}
          onClick={(event) => {
            // MUST call resolve() to open the native sheet. Withholding
            // it silently rejects the click so the gate (T&Cs) can
            // require user action elsewhere first without confusing the
            // wallet flow.
            if (!termsAccepted) return;
            event.resolve({});
          }}
          onConfirm={onWalletConfirm}
        />
      </div>

      {/* text-muted is a background colour in this theme (near invisible as
          text); read the muted ink token instead. Sentence case, no caps. */}
      <div className="my-4 flex items-center gap-3 text-xs" style={{ color: 'var(--fvd-ink-muted, var(--fv-text-secondary, #6B7280))' }}>
        <div className="flex-1 h-px" style={{ background: 'var(--fv-border, #e5e7eb)' }} />
        <span>Or pay with card</span>
        <div className="flex-1 h-px" style={{ background: 'var(--fv-border, #e5e7eb)' }} />
      </div>

      <PaymentElement
        options={{
          layout: 'accordion',
          paymentMethodOrder: ['card'],
          wallets: { applePay: 'never', googlePay: 'never' },
        }}
        onChange={(e) => onCardCompleteChange?.(e.complete)}
      />
    </>
  );
}
